'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { buildCoursesQuery } from '../../stores/jobportal-courses-store';
import { coursePage, parseCourseFilters } from '../../stores/course-list-url';
import { fetchCourses } from '@eliadmin/core/src/api/courses/jobportal-courses-api';
import { CourseCard } from '../courses/course-card';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

export type JobPortalCoursesContainerProps = {
  showPagination?: boolean;
  showLoadMore?: boolean;
  paginationKey?: string;
};

export function JobPortalCoursesContainer(props: JobPortalCoursesContainerProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const searchParams = useSearchParams();
  // One request identity, derived from the URL on SSR, navigation and refresh.
  // Never fetch the previous store filter while its URL-sync effect catches up.
  const filter = parseCourseFilters(searchParams);
  const currentPage = coursePage(searchParams.get(props.paginationKey || 'page'));
  const query = {
    ...buildCoursesQuery({ ...filter, is_paid: true }),
    'order[]': ['desc', filter.order || 'desc'] as ('asc' | 'desc')[],
    'orderBy[]': ['topped_up_to', filter.orderBy || 'created_at'] as const,
    search: filter.search,
    results: 30,
  };

  const {
    data: courses,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useSuspenseInfiniteQuery({
    queryKey: [CacheKey.COURSES, 'infinite', locale, authToken ?? null, currentPage, query],
    staleTime: authToken ? 0 : 60_000,
    queryFn: ({ pageParam }) =>
      fetchCourses({
        query: {
          ...query,
          'orderBy[]': [...query['orderBy[]']],
          page: pageParam,
        },
        locale,
        authToken,
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },

    initialPageParam: currentPage ? currentPage : 1,
  });

  const lastPage = courses?.pages[courses?.pages.length - 1];
  const flatList = courses?.pages.flatMap(page => page.data) ?? [];

  return (
    <div
      aria-busy={isFetching}
      className={cn(
        'relative flex w-full flex-col gap-6 @container',
        'ea-job-portal-courses-container',
      )}
    >
      {!isFetching && !flatList?.length && (
        <p className="text-center text-muted-foreground">{t('text554')}</p>
      )}

      {isFetching && !flatList.length && (
        <div role="status" aria-label="Načítavanie kurzov" className="flex justify-center py-8">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      )}

      {!!flatList.length && (
        <div
          className={cn(
            'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3',
            'ea-job-portal-courses-container__grid',
          )}
        >
          {flatList?.map((course, index) => (
            <CourseCard key={course.slug} course={course} priority={index === 0} headingLevel="h2" />
          ))}
        </div>
      )}

      {!!hasNextPage && props.showLoadMore && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-courses-container__load-more',
          )}
        >
          <Button variant="secondary" disabled={isFetching} onClick={() => fetchNextPage()}>
            {t('text338')}
          </Button>
        </div>
      )}

      {props.showPagination && lastPage?.meta.last_page > 1 && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-courses-container__pagination',
          )}
        >
          <Pagination links={lastPage?.meta.links} pageKey={props.paginationKey || 'page'} />
        </div>
      )}
    </div>
  );
}

export function JobPortalCoursesContainerSkeleton() {
  const array = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div
      className={cn('relative flex flex-col gap-6 @container', 'ea-job-portal-courses-container')}
    >
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5 sm:gap-y-8 lg:grid-cols-3',
          'ea-job-portal-courses-container__grid ea-job-portal-courses-container__grid--skeleton',
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className="min-h-[400px] min-w-[250px]" />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
